import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaMinus, FaMotorcycle, FaPlus, FaStore, FaShoppingBag, FaTrashAlt, FaWhatsapp } from "react-icons/fa";
import { useCarrinho } from "../context/CarrinhoContext";
import { useRestaurante } from "../context/RestauranteContext";
// Carrinho.css segue carregado: Login e Produção usam classes dele (.erro, .item-nome, .remover-item).
import "../css/Carrinho.css";
import "../css/Pedido.css";
import imageDefault from "../assets/no-image-default.png";
import { apiPublica } from "../services/api";
import { formatarPreco, parsePreco } from "../utils/preco";
import { situacaoFuncionamento } from "../utils/horarioFuncionamento";
import { FORMAS_PAGAMENTO, linkWhatsapp, montarMensagemPedido, subtotalDosItens } from "../utils/pedidoWhatsapp";
import { Botao, EstadoVazio } from "../components/ui";

// Dados do cliente ficam salvos no navegador para o próximo pedido (nome, telefone, endereço).
const CHAVE_CLIENTE = "geniusmenu:cliente";

const clienteInicial = {
  tipo: "entrega",
  nome: "",
  telefone: "",
  endereco: { cep: "", rua: "", numero: "", complemento: "", bairro: "", referencia: "" },
  pagamento: "pix",
  troco: "",
  observacao: "",
};

const lerClienteSalvo = () => {
  try {
    const salvo = JSON.parse(localStorage.getItem(CHAVE_CLIENTE) || "null");
    if (!salvo) return clienteInicial;
    return {
      ...clienteInicial,
      tipo: salvo.tipo === "retirada" ? "retirada" : "entrega",
      nome: salvo.nome || "",
      telefone: salvo.telefone || "",
      endereco: { ...clienteInicial.endereco, ...(salvo.endereco || {}) },
      pagamento: FORMAS_PAGAMENTO[salvo.pagamento] ? salvo.pagamento : "pix",
    };
  } catch {
    return clienteInicial;
  }
};

const salvarCliente = (c) => {
  try {
    // Observação e troco são do pedido, não do cliente: não ficam salvos.
    const { nome, telefone, endereco, tipo, pagamento } = c;
    localStorage.setItem(CHAVE_CLIENTE, JSON.stringify({ nome, telefone, endereco, tipo, pagamento }));
  } catch {
    // Sem localStorage (modo privado): só não lembra os dados no próximo pedido.
  }
};

const mascararTelefone = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

const mascararCep = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 8);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
};

const validar = (c) => {
  const erros = {};
  if (!c.nome.trim()) erros.nome = "Informe seu nome.";
  const tel = c.telefone.replace(/\D/g, "");
  if (tel.length < 10) erros.telefone = "Informe um telefone com DDD.";
  if (c.tipo === "entrega") {
    if (c.endereco.cep.replace(/\D/g, "").length !== 8) erros.cep = "Informe o CEP completo.";
    if (!c.endereco.rua.trim()) erros.rua = "Informe a rua.";
    if (!c.endereco.numero.trim()) erros.numero = "Informe o número (ou \"s/n\").";
    if (!c.endereco.bairro.trim()) erros.bairro = "Informe o bairro.";
  }
  return erros;
};

const Campo = ({ id, label, erro, dica, meio, children }) => (
  <div className={`pedido__campo${meio ? " pedido__campo--meio" : ""}`}>
    <label htmlFor={id}>{label}</label>
    {children}
    {erro ? (
      <p className="pedido__campo-erro" id={`${id}-erro`}>
        {erro}
      </p>
    ) : (
      dica && <p className="pedido__campo-dica">{dica}</p>
    )}
  </div>
);

const Carrinho = () => {
  const { carrinho, incrementarItem, removerItem, removerItemCompleto, limparCarrinho, restauranteCarrinho } = useCarrinho();
  const { restauranteId: restauranteNavegacao } = useRestaurante();
  // O carrinho salvo sabe de qual restaurante são os itens, mesmo depois de recarregar a página.
  const restauranteId = restauranteCarrinho || restauranteNavegacao;
  const navigate = useNavigate();
  const uid = useId();

  const [cliente, setCliente] = useState(lerClienteSalvo);
  const [erros, setErros] = useState({});
  const [restaurante, setRestaurante] = useState(null);
  const [erroRestaurante, setErroRestaurante] = useState(false);
  const [erroEnvio, setErroEnvio] = useState("");
  const [buscandoCep, setBuscandoCep] = useState(false);

  // Busca o restaurante ao abrir a tela: o clique em "Enviar" abre o WhatsApp na hora
  // (abrir depois de esperar a rede é bloqueado como pop-up em vários celulares).
  useEffect(() => {
    if (!restauranteId) return undefined;
    const controller = new AbortController();
    setErroRestaurante(false);
    apiPublica
      .buscarRestaurante(restauranteId, controller.signal)
      .then((r) => setRestaurante(r))
      .catch(() => {
        if (!controller.signal.aborted) setErroRestaurante(true);
      });
    return () => controller.abort();
  }, [restauranteId]);

  const subtotal = useMemo(() => subtotalDosItens(carrinho), [carrinho]);
  const situacao = situacaoFuncionamento(restaurante?.operatingHours);
  const entrega = cliente.tipo === "entrega";

  const atualizar = (campo, valor) => {
    setCliente((c) => ({ ...c, [campo]: valor }));
    setErros((e) => ({ ...e, [campo]: undefined }));
  };

  const atualizarEndereco = (campo, valor) => {
    setCliente((c) => ({ ...c, endereco: { ...c.endereco, [campo]: valor } }));
    setErros((e) => ({ ...e, [campo]: undefined }));
  };

  // CEP completo preenche rua e bairro (ViaCEP); o cliente pode corrigir à mão.
  // Só a resposta do CEP digitado por último vale: uma resposta atrasada de um CEP anterior
  // não pode sobrescrever o endereço.
  const ultimoCep = useRef("");
  const aoMudarCep = async (valor) => {
    const cep = mascararCep(valor);
    atualizarEndereco("cep", cep);
    const digitos = cep.replace(/\D/g, "");
    ultimoCep.current = digitos;
    if (digitos.length !== 8) {
      setBuscandoCep(false);
      return;
    }
    setBuscandoCep(true);
    try {
      const resp = await fetch(`https://viacep.com.br/ws/${digitos}/json/`);
      const dados = await resp.json();
      if (ultimoCep.current !== digitos) return;
      if (!dados.erro) {
        setCliente((c) => ({
          ...c,
          endereco: {
            ...c.endereco,
            rua: dados.logradouro || c.endereco.rua,
            bairro: dados.bairro || c.endereco.bairro,
          },
        }));
        setErros((e) => ({ ...e, rua: undefined, bairro: undefined }));
      }
    } catch {
      // Sem internet ou ViaCEP fora: o cliente preenche à mão.
    } finally {
      if (ultimoCep.current === digitos) setBuscandoCep(false);
    }
  };

  const enviarPedido = () => {
    setErroEnvio("");
    const encontrados = validar(cliente);
    setErros(encontrados);
    if (Object.keys(encontrados).length > 0) {
      const primeiro = Object.keys(encontrados)[0];
      document.getElementById(`${uid}-${primeiro}`)?.focus();
      return;
    }
    const mensagem = montarMensagemPedido({ itens: carrinho, cliente, restauranteNome: restaurante?.nome });
    const link = linkWhatsapp(restaurante?.numeroRestaurante, mensagem);
    if (!link) {
      setErroEnvio("Não foi possível encontrar o WhatsApp do restaurante. Tente de novo em instantes.");
      return;
    }
    salvarCliente(cliente);
    limparCarrinho();
    const janela = window.open(link, "_blank");
    if (!janela) {
      // Pop-up bloqueado: abre o WhatsApp na própria aba.
      window.location.href = link;
      return;
    }
    navigate(restauranteId ? `/${restauranteId}/cardapio` : "/restaurantes");
  };

  if (carrinho.length === 0) {
    return (
      <div className="pedido pedido__vazio">
        <EstadoVazio
          icone={<FaShoppingBag />}
          titulo="Seu carrinho está vazio"
          texto="Escolha os produtos no cardápio e eles aparecem aqui."
          acao={
            <Botao onClick={() => navigate(restauranteId ? `/${restauranteId}/cardapio` : "/restaurantes")}>
              {restauranteId ? "Ver cardápio" : "Ver restaurantes"}
            </Botao>
          }
        />
      </div>
    );
  }

  const idCampo = (nome) => `${uid}-${nome}`;
  const invalido = (nome) => (erros[nome] ? { "aria-invalid": "true", "aria-describedby": `${idCampo(nome)}-erro` } : {});

  return (
    <div className="pedido">
      <h1 className="pedido__titulo">Seu pedido</h1>

      {restaurante && (
        <div className="pedido__restaurante">
          <img src={restaurante.logoImage || imageDefault} alt="" />
          <div>
            <p className="pedido__restaurante-nome">{restaurante.nome}</p>
            {situacao && (
              <p className={`pedido__restaurante-status${situacao.aberto ? " aberto" : ""}`}>
                {situacao.aberto ? "Aberto agora" : "Fechado agora"}
              </p>
            )}
          </div>
        </div>
      )}

      {situacao && !situacao.aberto && (
        <p className="pedido__aviso">
          O restaurante está fechado agora. Você pode enviar o pedido, mas ele só será respondido quando abrir.
        </p>
      )}

      <section className="pedido__bloco" aria-labelledby={idCampo("itens-titulo")}>
        <h2 className="pedido__bloco-titulo" id={idCampo("itens-titulo")}>Itens</h2>
        <ul className="pedido__itens">
          {carrinho.map((item) => (
            <li key={item.chave} className="pedido__item">
              <img
                src={(Array.isArray(item.imagem) ? item.imagem[0] : item.imagem) || imageDefault}
                alt=""
                loading="lazy"
                decoding="async"
              />
              <div>
                <p className="pedido__item-nome">{item.nome}</p>
                {item.observacao && <p className="pedido__item-obs">Obs.: {item.observacao}</p>}
                <p className="pedido__item-preco">{formatarPreco(parsePreco(item.preco) * item.quantidade)}</p>
              </div>
              <div className="pedido__qtd" role="group" aria-label={`Quantidade de ${item.nome}`}>
                {item.quantidade === 1 ? (
                  <button type="button" onClick={() => removerItemCompleto(item.chave)} aria-label={`Remover ${item.nome}`}>
                    <FaTrashAlt aria-hidden="true" />
                  </button>
                ) : (
                  <button type="button" onClick={() => removerItem(item.chave)} aria-label={`Diminuir ${item.nome}`}>
                    <FaMinus aria-hidden="true" />
                  </button>
                )}
                <span aria-live="polite">{item.quantidade}</span>
                <button type="button" onClick={() => incrementarItem(item.chave)} aria-label={`Aumentar ${item.nome}`}>
                  <FaPlus aria-hidden="true" />
                </button>
              </div>
            </li>
          ))}
        </ul>
        {restauranteId && (
          <Botao
            variante="texto"
            className="pedido__mais-itens"
            icone={<FaPlus />}
            onClick={() => navigate(`/${restauranteId}/cardapio`)}
          >
            Adicionar mais itens
          </Botao>
        )}
      </section>

      <section className="pedido__bloco" aria-labelledby={idCampo("receber-titulo")}>
        <h2 className="pedido__bloco-titulo" id={idCampo("receber-titulo")}>Como você quer receber?</h2>
        <div className="pedido__opcoes" role="radiogroup" aria-labelledby={idCampo("receber-titulo")}>
          {[
            { valor: "entrega", rotulo: "Entrega", icone: <FaMotorcycle /> },
            { valor: "retirada", rotulo: "Retirar no local", icone: <FaStore /> },
          ].map((op) => (
            <div className="pedido__opcao" key={op.valor}>
              <input
                type="radio"
                id={idCampo(`tipo-${op.valor}`)}
                name={idCampo("tipo")}
                value={op.valor}
                checked={cliente.tipo === op.valor}
                onChange={() => atualizar("tipo", op.valor)}
              />
              <label htmlFor={idCampo(`tipo-${op.valor}`)}>
                <span aria-hidden="true">{op.icone}</span>
                {op.rotulo}
              </label>
            </div>
          ))}
        </div>
        {!entrega && restaurante?.endereco && (
          <p className="pedido__retirada-endereco">
            Retirada em: {[restaurante.endereco.rua, restaurante.endereco.numero].filter(Boolean).join(", ")}
            {restaurante.endereco.bairro ? ` - ${restaurante.endereco.bairro}` : ""}
          </p>
        )}

        <div className="pedido__campos">
          <Campo id={idCampo("nome")} label="Seu nome" erro={erros.nome}>
            <input
              id={idCampo("nome")}
              autoComplete="name"
              value={cliente.nome}
              onChange={(e) => atualizar("nome", e.target.value)}
              {...invalido("nome")}
            />
          </Campo>
          <Campo id={idCampo("telefone")} label="Telefone (WhatsApp)" erro={erros.telefone}>
            <input
              id={idCampo("telefone")}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="(00) 00000-0000"
              value={cliente.telefone}
              onChange={(e) => atualizar("telefone", mascararTelefone(e.target.value))}
              {...invalido("telefone")}
            />
          </Campo>

          {entrega && (
            <>
              <Campo id={idCampo("cep")} label="CEP" meio erro={erros.cep} dica={buscandoCep ? "Buscando endereço..." : "Preenche rua e bairro"}>
                <input
                  id={idCampo("cep")}
                  inputMode="numeric"
                  autoComplete="postal-code"
                  placeholder="00000-000"
                  value={cliente.endereco.cep}
                  onChange={(e) => aoMudarCep(e.target.value)}
                  {...invalido("cep")}
                />
              </Campo>
              <Campo id={idCampo("numero")} label="Número" meio erro={erros.numero}>
                <input
                  id={idCampo("numero")}
                  inputMode="text"
                  value={cliente.endereco.numero}
                  onChange={(e) => atualizarEndereco("numero", e.target.value)}
                  {...invalido("numero")}
                />
              </Campo>
              <Campo id={idCampo("rua")} label="Rua" erro={erros.rua}>
                <input
                  id={idCampo("rua")}
                  autoComplete="address-line1"
                  value={cliente.endereco.rua}
                  onChange={(e) => atualizarEndereco("rua", e.target.value)}
                  {...invalido("rua")}
                />
              </Campo>
              <Campo id={idCampo("bairro")} label="Bairro" meio erro={erros.bairro}>
                <input
                  id={idCampo("bairro")}
                  value={cliente.endereco.bairro}
                  onChange={(e) => atualizarEndereco("bairro", e.target.value)}
                  {...invalido("bairro")}
                />
              </Campo>
              <Campo id={idCampo("complemento")} label="Complemento" meio>
                <input
                  id={idCampo("complemento")}
                  autoComplete="address-line2"
                  placeholder="Apto, bloco..."
                  value={cliente.endereco.complemento}
                  onChange={(e) => atualizarEndereco("complemento", e.target.value)}
                />
              </Campo>
              <Campo id={idCampo("referencia")} label="Ponto de referência">
                <input
                  id={idCampo("referencia")}
                  value={cliente.endereco.referencia}
                  onChange={(e) => atualizarEndereco("referencia", e.target.value)}
                />
              </Campo>
            </>
          )}
        </div>
      </section>

      <section className="pedido__bloco" aria-labelledby={idCampo("pagamento-titulo")}>
        <h2 className="pedido__bloco-titulo" id={idCampo("pagamento-titulo")}>Pagamento</h2>
        <div className="pedido__opcoes pedido__opcoes--3" role="radiogroup" aria-labelledby={idCampo("pagamento-titulo")}>
          {[
            { valor: "pix", rotulo: "Pix" },
            { valor: "cartao", rotulo: "Cartão" },
            { valor: "dinheiro", rotulo: "Dinheiro" },
          ].map((op) => (
            <div className="pedido__opcao" key={op.valor}>
              <input
                type="radio"
                id={idCampo(`pag-${op.valor}`)}
                name={idCampo("pagamento")}
                value={op.valor}
                checked={cliente.pagamento === op.valor}
                onChange={() => atualizar("pagamento", op.valor)}
              />
              <label htmlFor={idCampo(`pag-${op.valor}`)}>{op.rotulo}</label>
            </div>
          ))}
        </div>
        <div className="pedido__campos">
          {cliente.pagamento === "dinheiro" && (
            <Campo id={idCampo("troco")} label="Troco para quanto?" dica="Deixe em branco se não precisar de troco">
              <input
                id={idCampo("troco")}
                inputMode="decimal"
                placeholder="R$ 100,00"
                value={cliente.troco}
                onChange={(e) => atualizar("troco", e.target.value)}
              />
            </Campo>
          )}
          <Campo id={idCampo("observacao")} label="Observação do pedido">
            <textarea
              id={idCampo("observacao")}
              maxLength={200}
              placeholder="Ex.: interfone com defeito, tocar a campainha"
              value={cliente.observacao}
              onChange={(e) => atualizar("observacao", e.target.value)}
            />
          </Campo>
        </div>
      </section>

      <section className="pedido__bloco" aria-labelledby={idCampo("resumo-titulo")}>
        <h2 className="pedido__bloco-titulo" id={idCampo("resumo-titulo")}>Resumo</h2>
        <p className="pedido__resumo-linha">
          <span>Subtotal</span>
          <span>{formatarPreco(subtotal)}</span>
        </p>
        {entrega && (
          <p className="pedido__resumo-linha">
            <span>Entrega</span>
            <span>a combinar</span>
          </p>
        )}
        <p className="pedido__resumo-total">
          <span>Total{entrega ? " + entrega" : ""}</span>
          <span>{formatarPreco(subtotal)}</span>
        </p>
        {entrega && (
          <p className="pedido__resumo-nota">O restaurante informa o valor da entrega pelo WhatsApp.</p>
        )}
      </section>

      {(erroEnvio || erroRestaurante) && (
        <p className="pedido__erro-envio" role="alert">
          {erroEnvio || "Não foi possível carregar os dados do restaurante. Verifique sua conexão."}
        </p>
      )}

      <div className="pedido__acao">
        <Botao tamanho="lg" largo onClick={enviarPedido} disabled={!restaurante} icone={<FaWhatsapp />}>
          <span>Enviar pedido</span>
          <span>{formatarPreco(subtotal)}</span>
        </Botao>
      </div>
    </div>
  );
};

export default Carrinho;
